import { useCallback, useRef, useState } from 'react';

import { apiClient } from '@config/server/apiClient';
import { authEndpoints } from '@config/server/apiEndpoints';

interface DocsTokenResponse {
	url: string;
}

interface UseMintlifyDocsTokenReturn {
	fetchAndRedirect: (redirectParam: string | null) => Promise<void>;
	isFetching: boolean;
	error: string | null;
	clearError: () => void;
	setError: (error: string) => void;
	/** Whether we've already attempted to fetch the token (prevents infinite retries) */
	hasAttempted: boolean;
}

/**
 * Hook for fetching Mintlify docs token and redirecting
 * Handles the API call and error states for the Mintlify login flow
 */
export function useMintlifyDocsToken(): UseMintlifyDocsTokenReturn {
	const [error, setError] = useState<string | null>(null);
	const [isFetching, setIsFetching] = useState(false);
	// Track if we've attempted to prevent infinite loops on error
	const hasAttemptedRef = useRef(false);
	const [hasAttempted, setHasAttempted] = useState(false);

	const fetchAndRedirect = useCallback(async (redirectParam: string | null) => {
		// Prevent multiple attempts - once we've tried, don't try again automatically
		if (hasAttemptedRef.current) {
			return;
		}
		hasAttemptedRef.current = true;
		setHasAttempted(true);
		setIsFetching(true);
		setError(null);

		try {
			// Call the docs token endpoint
			// apiClient already has the token set by AuthSessionProvider
			const response = await apiClient.post<DocsTokenResponse>(
				authEndpoints.docsToken(),
				{ redirect: redirectParam || undefined },
			);

			if (response.success && response.data?.url) {
				// Redirect to the Mintlify callback URL with JWT
				// Note: This is an external URL (Mintlify), so we must use window.location
				// navigate() from react-router-dom only works for internal routes
				 
				window.location.href = response.data.url;
			} else {
				setError('Failed to generate docs token. Please try again.');
				setIsFetching(false);
			}
		} catch (err: unknown) {
			console.error('Error fetching docs token:', err);
			const apiError = err as { message?: string; status?: number; details?: unknown };

			// Handle 403 Forbidden (no services provider access)
			if (apiError.status === 403) {
				setError('Access denied. Your account does not have access to documentation.');
			} else if (apiError.status === 400) {
				setError('Invalid redirect URL. Please contact support.');
			} else {
				setError(
					apiError.message || 'Failed to generate docs token. Please try again.',
				);
			}
			setIsFetching(false);
		}
	}, []);

	const clearError = useCallback(() => {
		setError(null);
		// Reset attempt flag when clearing error so user can retry
		hasAttemptedRef.current = false;
		setHasAttempted(false);
	}, []);

	const setErrorManually = useCallback((errorMessage: string) => {
		setError(errorMessage);
	}, []);

	return {
		fetchAndRedirect,
		isFetching,
		error,
		clearError,
		setError: setErrorManually,
		hasAttempted,
	};
}
